import type { ApiErrorBody, ErrorCode } from '@scan2eat/shared';
import { CART_STORAGE_KEY, SESSION_TOKEN_HEADER, SESSION_TOKEN_STORAGE_KEY } from '@scan2eat/shared';

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

export class ApiError extends Error {
  constructor(
    public readonly code: ErrorCode,
    public readonly httpStatus: number,
    message: string,
    public readonly details: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function getSessionToken(): string | null {
  try {
    return localStorage.getItem(SESSION_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function handleSessionExpired(): never {
  try {
    localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
    // Par la constante : depuis le renommage (ADR 0020), la cle en dur
    // 'emenu.cart' ne correspondait plus au panier et ne le vidait plus.
    localStorage.removeItem(CART_STORAGE_KEY);
  } catch {
    /* noop */
  }
  window.location.href = '/expire';
  throw new ApiError('SESSION_EXPIRED', 401, 'Session expiree');
}

/** Lit le corps d'erreur sans decider quoi en faire. */
async function parseError(res: Response): Promise<ApiError> {
  try {
    const body: ApiErrorBody = await res.json();
    return new ApiError(body.error.code, res.status, body.error.message, body.error.details);
  } catch {
    return new ApiError('INTERNAL_ERROR', res.status, `Erreur HTTP ${res.status}`);
  }
}

/**
 * Rattrape une session fermee sous les pieds du convive.
 *
 * Une session se ferme dix minutes apres que le service y est solde, sans
 * prevenir : un convive qui lit longuement la carte peut donc valider son
 * panier sur une session close. On rouvre alors depuis le QR code memorise et
 * on rejoue la requete, une seule fois. Le convive ne voit rien.
 *
 * Si la reouverture echoue -- QR tourne, table hors service, aucun jeton
 * memorise -- on retombe sur l'ecran « rescannez le QR code ».
 *
 * Voir docs/adr/0013-cloture-de-session-au-service-solde.md.
 */
async function recoverSession(): Promise<boolean> {
  const { ensureOrderableSession } = await import('../lib/session.js');
  return (await ensureOrderableSession()) === 'ready';
}

export async function apiFetch<T>(path: string, init?: RequestInit, retried = false): Promise<T> {
  const token = getSessionToken();
  const headers = new Headers(init?.headers);
  headers.set('Accept', 'application/json');
  if (token) headers.set(SESSION_TOKEN_HEADER, token);

  const res = await fetch(`${BASE_URL}${path}`, { ...init, headers, credentials: 'include' });

  if (!res.ok) {
    const error = await parseError(res);
    if (error.code === 'SESSION_EXPIRED') {
      if (!retried && (await recoverSession())) return apiFetch<T>(path, init, true);
      handleSessionExpired();
    }
    throw error;
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export async function apiFetchBlob(path: string, retried = false): Promise<Blob> {
  const token = getSessionToken();
  const headers = new Headers();
  if (token) headers.set(SESSION_TOKEN_HEADER, token);

  const res = await fetch(`${BASE_URL}${path}`, { headers, credentials: 'include' });

  if (!res.ok) {
    const error = await parseError(res);
    if (error.code === 'SESSION_EXPIRED') {
      if (!retried && (await recoverSession())) return apiFetchBlob(path, true);
      handleSessionExpired();
    }
    throw error;
  }

  return res.blob();
}
